import * as React from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { F0Dialog } from '@factorialco/f0-react'
import { RichTextDisplay } from '@factorialco/f0-react/dist/experimental'
import {
  MarkProcurementTaskAsDoneDocument,
  PurchaseRequestDetailDocument,
  PurchaseRequestFragment,
} from 'generated/resources/graphql'

import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

interface ProcurementTaskModalProps {
  isOpen: boolean
  onClose: () => void
  procurementTask: PurchaseRequestFragment['task'][number] | null | undefined
}

const ProcurementTaskModal: React.FC<ProcurementTaskModalProps> = ({
  isOpen,
  onClose,
  procurementTask,
}) => {
  const [markAsDone, { loading: isMarkingAsDone }] = useMutation(
    MarkProcurementTaskAsDoneDocument,
    {
      extractorFn: (data) => data.procurementMutations.markAsDoneProcurementTask,
      refetchQueries: [PurchaseRequestDetailDocument],
    }
  )

  const handleConfirm = useUnsafeCallback(async () => {
    if (!procurementTask?.id) return

    const result = await markAsDone({
      variables: {
        taskId: procurementTask.id,
      },
    })

    const handled = handleResult(result, () => {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('procurement.purchase_requests.detail.responses.general_task.error'),
      })
    })

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('procurement.purchase_requests.detail.responses.general_task.success'),
      })
      onClose()
    }
  }, [procurementTask?.id, markAsDone, onClose])

  if (!procurementTask) return null

  return (
    <F0Dialog
      isOpen={isOpen}
      onClose={onClose}
      title={
        procurementTask.taskName ||
        i18n.t('procurement.purchase_requests.detail.procurement_task_modal.title')
      }
      primaryAction={{
        label: i18n.t('procurement.purchase_requests.detail.procurement_task_modal.confirm'),
        onClick: handleConfirm,
        loading: isMarkingAsDone,
        disabled: isMarkingAsDone,
      }}
    >
      {procurementTask.taskContent && (
        <Box flexDirection="column" gap="s4">
          <RichTextDisplay
            content={procurementTask.taskContent}
            className="text-f1-foreground-secondary"
          />
        </Box>
      )}
    </F0Dialog>
  )
}

export default ProcurementTaskModal
